import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { useTranslation } from '../hooks/useTranslation';
import { tithiTamil, nakshatraTamil, yogaTamil, pakshaTamil, rashiTamil, tamilYogaTamil, translatePanchangValue } from '../data/panchangTamil';
import { panchangAPI } from '../services/api';
import axios from 'axios';
import './Panchangam.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const offsetIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const Panchangam = () => {
  const routerLocation = useLocation();
  const [panchangData, setPanchangData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cityInput, setCityInput] = useState('Chennai');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const suggestionsRef = useRef(null);
  const hydrated = useHydrated();
  const { t, lang } = useTranslation();

  const tp = (value, mapping) => lang === 'ta' ? translatePanchangValue(value, mapping) : value;

  // Initial load + pre-fill from PSEO navigation state
  useEffect(() => {
    const ls = routerLocation.state;
    if (ls && (ls.city || ls.date)) {
      const cityName = ls.city || 'Chennai';
      const date = ls.date || todayIso();
      setCityInput(cityName);
      setSelectedDate(date);
      fetchPanchangam(cityName, null, date);
      window.history.replaceState({}, document.title);
    } else {
      fetchPanchangam('Chennai', null, todayIso());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPanchangam = async (cityName, locData, dateStr) => {
    setLoading(true);
    setError(null);
    const isToday = !dateStr || dateStr === todayIso();
    const endpoint = isToday ? 'today' : 'date';
    try {
      const params = {};
      if (!isToday) params.date = dateStr;
      if (locData) {
        params.lat = locData.latitude;
        params.lon = locData.longitude;
        params.tz = locData.timezone;
        params.city = locData.city;
        params.state = locData.state;
        params.country = locData.country;
      } else {
        params.location = cityName;
      }
      const response = await axios.get(`${API_BASE_URL}/panchang/${endpoint}`, { params });
      setPanchangData(response.data.data);
    } catch (err) {
      setError('Unable to fetch panchangam data. Please ensure the server is running on port 5000.');
      console.error('Panchangam fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchCities = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/panchang/cities`, {
        params: { q: query }
      });
      setSuggestions(response.data.data || []);
      setShowSuggestions(true);
    } catch (err) {
      setSuggestions([]);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (cityInput.length >= 2) {
        searchCities(cityInput);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [cityInput, searchCities]);

  const handleCitySelect = (city) => {
    const displayName = city.state
      ? `${city.city}, ${city.state}, ${city.country}`
      : `${city.city}, ${city.country}`;
    setCityInput(displayName);
    setSelectedLocation(city);
    setShowSuggestions(false);
    fetchPanchangam(city.city, city, selectedDate);
  };

  const handleSearch = () => {
    setShowSuggestions(false);
    if (selectedLocation) {
      fetchPanchangam(selectedLocation.city, selectedLocation, selectedDate);
    } else {
      fetchPanchangam(cityInput, null, selectedDate);
    }
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    if (selectedLocation) {
      fetchPanchangam(selectedLocation.city, selectedLocation, newDate);
    } else {
      fetchPanchangam(cityInput, null, newDate);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const renderTimeRange = (label, data) => (
    <div className="panchang-row">
      <span className="panchang-label">{label} :</span>
      <span className="panchang-value">{data.start} - {data.end}</span>
    </div>
  );

  return (
    <div className="panchangam-page">
      <SEO
        title="Today's Panchang - Daily Hindu Calendar & Timings"
        description="Get today's accurate Panchang with tithi, nakshatra, yoga, rahu kalam, gulika kalam, yamaganda, and abhijit muhurta timings for your city. Calculated using Vedic astronomy."
        keywords="panchang today, daily panchang, hindu calendar, tithi today, nakshatra today, rahu kalam, good time today, panchangam"
        canonicalPath="/panchangam"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Daily Panchangam Calculator",
          "applicationCategory": "LifestyleApplication",
          "description": "Daily Hindu calendar with tithi, nakshatra, yoga, and auspicious timings",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the difference between Panchang and Panchangam?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Panchang (Hindi/Sanskrit) and Panchangam (Tamil/Telugu/Kannada) both refer to the same traditional Hindu almanac. The word means five limbs (Pancha = five, Anga = limb), referring to the five elements: Tithi, Nakshatra, Yoga, Karana, and Vara. Regional panchangams may differ slightly in calculation methods but follow the same Vedic principles."
              }
            },
            {
              "@type": "Question",
              "name": "How is Rahu Kalam calculated?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Rahu Kalam is calculated by dividing the day (sunrise to sunset) into 8 equal parts. The specific part that falls under Rahu's influence changes each day of the week. On Monday it is the 2nd part, Tuesday the 7th, Wednesday the 5th, Thursday the 6th, Friday the 4th, Saturday the 3rd, and Sunday the 8th. The exact time varies by location and season based on local sunrise."
              }
            },
            {
              "@type": "Question",
              "name": "What is Abhijit Muhurta?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Abhijit Muhurta is considered the most auspicious time of any day, occurring approximately at solar noon (about 48 minutes centered on local midday). It is so powerful that it overrides most inauspicious Tithis, Nakshatras, and even Rahu Kalam. However, it is not available on Wednesdays and is avoided when the Moon is in Abhijit Nakshatra. It is ideal for starting important work, travel, and ceremonies."
              }
            },
            {
              "@type": "Question",
              "name": "What is Chandrashtama and should I be concerned?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Chandrashtama occurs when the Moon transits through the 8th sign from your natal Moon sign. It lasts approximately 2.25 days each month. During this period, one may experience mental stress, physical discomfort, and unfavorable outcomes. It is advisable to avoid starting new ventures, travel, medical procedures, or important decisions during Chandrashtama. Rest, prayer, and meditation are recommended."
              }
            }
          ]
        }]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1>{lang === 'ta' ? 'இன்றைய' : 'Today'} <span className="gradient-text">{lang === 'ta' ? 'பஞ்சாங்கம்' : 'Panchang'}</span></h1>
          <p>{lang === 'ta' ? 'திதி, நட்சத்திரம், யோகம் மற்றும் சுப நேரங்களுடன் தினசரி இந்து நாட்காட்டி' : 'Daily Hindu calendar with tithi, nakshatra, yoga, and auspicious timings'}</p>
        </motion.div>

        <motion.div
          className="panchangam-container"
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="panchangam-card">
            <div className="panchangam-header">
              <h2>{lang === 'ta' ? 'இன்றைய பஞ்சாங்கம்' : 'Today Panchang'}</h2>
            </div>

            <div className="city-selector" ref={suggestionsRef}>
              <div className="city-search-wrapper">
                <div className="city-input-wrap">
                  <input
                    type="text"
                    value={cityInput}
                    onChange={(e) => {
                      setCityInput(e.target.value);
                      setSelectedLocation(null);
                    }}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search city globally..."
                    className="city-input"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="city-suggestions">
                      {suggestions.map((city, idx) => (
                        <li
                          key={`${city.city}-${city.country}-${idx}`}
                          onMouseDown={() => handleCitySelect(city)}
                          className="city-suggestion-item"
                        >
                          <span className="suggestion-city">{city.city}</span>
                          <span className="suggestion-detail">
                            {city.state ? `${city.state}, ` : ''}{city.country}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="date-input"
                  aria-label="Select date"
                />
                <button onClick={handleSearch} className="city-search-btn">
                  Go
                </button>
              </div>

              <div className="date-quick-buttons">
                <button
                  type="button"
                  className={`date-quick-btn ${selectedDate === offsetIso(-1) ? 'active' : ''}`}
                  onClick={() => handleDateChange(offsetIso(-1))}
                >
                  {lang === 'ta' ? 'நேற்று' : 'Yesterday'}
                </button>
                <button
                  type="button"
                  className={`date-quick-btn ${selectedDate === todayIso() ? 'active' : ''}`}
                  onClick={() => handleDateChange(todayIso())}
                >
                  {lang === 'ta' ? 'இன்று' : 'Today'}
                </button>
                <button
                  type="button"
                  className={`date-quick-btn ${selectedDate === offsetIso(1) ? 'active' : ''}`}
                  onClick={() => handleDateChange(offsetIso(1))}
                >
                  {lang === 'ta' ? 'நாளை' : 'Tomorrow'}
                </button>
                <button
                  type="button"
                  className={`date-quick-btn ${selectedDate === offsetIso(7) ? 'active' : ''}`}
                  onClick={() => handleDateChange(offsetIso(7))}
                >
                  +7 {lang === 'ta' ? 'நாட்கள்' : 'days'}
                </button>
              </div>
            </div>

            {loading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>{lang === 'ta' ? 'பஞ்சாங்கம் கணக்கிடப்படுகிறது...' : 'Calculating panchangam...'}</p>
              </div>
            )}

            {error && (
              <div className="error-state">
                <p>{error}</p>
                <button onClick={handleSearch} className="btn btn-primary">
                  Retry
                </button>
              </div>
            )}

            {panchangData && !loading && (
              <div className="panchangam-content">
                <div className="panchang-row">
                  <span className="panchang-label">{lang === 'ta' ? 'நகரம்' : 'City'} :</span>
                  <span className="panchang-value">
                    {panchangData.location.city}
                    {panchangData.location.state ? `, ${panchangData.location.state}` : ''}
                    {panchangData.location.country ? `, ${panchangData.location.country}` : ''}
                  </span>
                </div>

                <div className="panchang-row">
                  <span className="panchang-label">{lang === 'ta' ? 'தேதி' : 'Date'} :</span>
                  <span className="panchang-value">{panchangData.date}</span>
                </div>

                <div className="panchang-row">
                  <span className="panchang-label">{lang === 'ta' ? 'சூரிய உதயம்' : 'Sunrise'} :</span>
                  <span className="panchang-value">{panchangData.sunrise}</span>
                  <span className="panchang-label" style={{ marginLeft: '15px' }}>{lang === 'ta' ? 'சூரிய அஸ்தமனம்' : 'Sunset'} :</span>
                  <span className="panchang-value">{panchangData.sunset}</span>
                </div>

                <div className="panchang-row">
                  <span className="panchang-label panchang-link">{lang === 'ta' ? 'திதி' : 'Tithi'} {tp(panchangData.tithi.paksha, pakshaTamil)} {lang === 'ta' ? 'பக்ஷம்' : 'Paksham'} :</span>
                  <span className="panchang-value">
                    {tp(panchangData.tithi.name, tithiTamil)} - {lang === 'ta' ? 'வரை' : 'till'} {panchangData.tithi.endTime}
                    {' '}{lang === 'ta' ? 'அடுத்தது' : 'Followed By'} {tp(panchangData.tithi.followedBy, tithiTamil)}
                  </span>
                </div>

                <div className="panchang-row">
                  <span className="panchang-label panchang-link">{lang === 'ta' ? 'நட்சத்திரம்' : 'Nakshatra'} :</span>
                  <span className="panchang-value">
                    {tp(panchangData.nakshatra.name, nakshatraTamil)} - {lang === 'ta' ? 'வரை' : 'till'} {panchangData.nakshatra.endTime}
                    {' '}{lang === 'ta' ? 'அடுத்தது' : 'Followed By'} {tp(panchangData.nakshatra.followedBy, nakshatraTamil)}
                  </span>
                </div>

                <div className="panchang-row">
                  <span className="panchang-label panchang-link">{lang === 'ta' ? 'தமிழ் யோகம்' : 'Tamil Yoga'} :</span>
                  <span className="panchang-value">
                    {tp(panchangData.tamilYoga.name, tamilYogaTamil)} - {tp(panchangData.yoga.name, yogaTamil)} {lang === 'ta' ? 'வரை' : 'till'} {panchangData.yoga.endTime}
                    {' '}{lang === 'ta' ? 'அடுத்தது' : 'Followed By'} {tp(panchangData.yoga.followedBy, yogaTamil)}
                  </span>
                </div>

                {renderTimeRange(lang === 'ta' ? 'ராகு காலம்' : 'Rahu Kalam', panchangData.rahuKalam)}
                {renderTimeRange(lang === 'ta' ? 'குளிகை காலம்' : 'Gulikai Kalam', panchangData.gulikaKalam)}
                {renderTimeRange(lang === 'ta' ? 'எமகண்டம்' : 'Yamaganda', panchangData.yamaganda)}
                {renderTimeRange(lang === 'ta' ? 'அபிஜித் முகூர்த்தம்' : 'Abhijit', panchangData.abhijitMuhurta)}

                <div className="panchang-row">
                  <span className="panchang-label panchang-link">{lang === 'ta' ? 'சந்திராஷ்டமம்' : 'Chandrashtama'} :</span>
                  <span className="panchang-value">
                    {tp(panchangData.chandrashtama.affectedRashi, rashiTamil)} {lang === 'ta' ? 'ராசி' : 'rasi'}
                    {' '}({panchangData.chandrashtama.nakshatrasInAffectedRashi})
                  </span>
                </div>
              </div>
            )}

            <div className="panchangam-footer">
              <button
                className="btn btn-primary btn-find-city"
                onClick={() => document.querySelector('.city-input').focus()}
              >
                {lang === 'ta' ? 'உங்கள் நகர பஞ்சாங்கத்தைக் காணுங்கள்' : 'Find your city panchangam'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>Today's Panchang - Hindu Daily Calendar & Auspicious Timings</h2>
          <p>Panchang (also spelled Panchangam) is the traditional Hindu calendar that provides five essential elements for each day: Tithi (lunar day), Nakshatra (birth star), Yoga, Karana, and Vara (weekday). These five components help determine auspicious and inauspicious timings for daily activities, rituals, and ceremonies.</p>
          <p>Our daily Panchang includes Rahu Kalam, Gulikai Kalam, and Yamaganda timings — the inauspicious periods to avoid for starting new ventures. It also shows Abhijit Muhurta, the most auspicious time of the day, and Chandrashtama details for your Moon sign. Use AstroEpanchang's Panchang to plan your day according to Vedic astrological principles.</p>

          <h3>The Five Elements of Panchang Explained</h3>
          <p><strong>Tithi (Lunar Day):</strong> The Tithi is the angular relationship between the Sun and Moon, advancing 12° for each Tithi. There are 30 Tithis in a lunar month — 15 in the waxing phase (Shukla Paksha) and 15 in the waning phase (Krishna Paksha). Each Tithi has a specific character — some are auspicious for beginnings (Pratipad, Panchami, Dashami), while others like Ashtami, Chaturdashi, and Amavasya are avoided for new ventures. The Tithi governs the right time for sacred rituals, fasting (Vrat), and ceremonies.</p>
          <p><strong>Nakshatra (Lunar Mansion):</strong> The Nakshatra is the constellation where the Moon is transiting. There are 27 Nakshatras, each spanning 13°20' of the zodiac. Each Nakshatra has a presiding deity, ruling planet, and associated activities. Ashwini, Pushya, and Hasta are considered highly auspicious for starting new projects, travel, and medical treatments. Nakshatras form the basis for birth star predictions, muhurtham selection, and marriage compatibility.</p>
          <p><strong>Yoga & Karana:</strong> Yoga is calculated from the combined longitude of the Sun and Moon. There are 27 Yogas, ranging from highly auspicious (Siddha, Shubha, Amrita) to inauspicious (Vishkambha, Vyatipata, Parigha). The Karana is half a Tithi (approximately 6 hours), and there are 11 Karanas in total — 4 fixed and 7 movable. Bhadra Karana (Vishti) is considered inauspicious and should be avoided for important work.</p>

          <h3>Understanding Rahu Kalam, Gulikai & Yamaganda</h3>
          <p>Rahu Kalam is an inauspicious period of approximately 1.5 hours that occurs daily. It is calculated based on the day of the week and local sunrise time. Monday's Rahu Kalam falls in the morning, while Friday's occurs in the afternoon. Starting new work, travel, or important tasks during Rahu Kalam is traditionally avoided. Similarly, Gulikai Kalam (associated with Saturn's son Gulika) and Yamaganda (associated with Yama, the god of death) are other inauspicious periods to observe. Knowing these timings helps you plan your day for maximum auspiciousness.</p>

          <h3>Frequently Asked Questions About Panchang</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>What is the difference between Panchang and Panchangam?</h4>
              <p>Panchang (Hindi/Sanskrit) and Panchangam (Tamil/Telugu/Kannada) both refer to the same traditional Hindu almanac. The word means "five limbs" (Pancha = five, Anga = limb), referring to the five elements: Tithi, Nakshatra, Yoga, Karana, and Vara. Regional panchangams may differ slightly in calculation methods but follow the same Vedic principles.</p>
            </div>
            <div className="faq-seo-item">
              <h4>How is Rahu Kalam calculated?</h4>
              <p>Rahu Kalam is calculated by dividing the day (sunrise to sunset) into 8 equal parts. The specific part that falls under Rahu's influence changes each day of the week. On Monday it is the 2nd part, Tuesday the 7th, Wednesday the 5th, Thursday the 6th, Friday the 4th, Saturday the 3rd, and Sunday the 8th. The exact time varies by location and season based on local sunrise.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What is Abhijit Muhurta?</h4>
              <p>Abhijit Muhurta is considered the most auspicious time of any day, occurring approximately at solar noon (about 48 minutes centered on local midday). It is so powerful that it overrides most inauspicious Tithis, Nakshatras, and even Rahu Kalam. However, it is not available on Wednesdays and is avoided when the Moon is in Abhijit Nakshatra. It is ideal for starting important work, travel, and ceremonies.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What is Chandrashtama and should I be concerned?</h4>
              <p>Chandrashtama occurs when the Moon transits through the 8th sign from your natal Moon sign. It lasts approximately 2.25 days each month. During this period, one may experience mental stress, physical discomfort, and unfavorable outcomes. It is advisable to avoid starting new ventures, travel, medical procedures, or important decisions during Chandrashtama. Rest, prayer, and meditation are recommended.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Panchangam;
